import { useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useAuthStore } from '@/stores/auth.store'
import {
    Users,
    Car,
    FileText,
    Receipt,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    CheckCircle,
    Clock,
    Activity,
} from 'lucide-react'
import { formatCurrency } from '@/lib/formatters'

// Dados mock para demonstração —  serão substituídos por queries reais
const mockStats = {
    totalAssociados: 1247,
    associadosAtivos: 1089,
    associadosInadimplentes: 128,
    totalVeiculos: 1583,
    contratosAtivos: 1089,
    faturasEmAberto: 342,
    receitaMes: 189750.0,
    inadimplencia: 10.3,
}

const recentActivities = [
    { id: 1, tipo: 'contrato.criado', descricao: 'Novo contrato — João Silva', tempo: 'há 2 min', cor: 'text-success-500' },
    { id: 2, tipo: 'pagamento.confirmado', descricao: 'Pagamento recebido — Maria Santos', tempo: 'há 15 min', cor: 'text-primary-500' },
    { id: 3, tipo: 'integracao.falha', descricao: 'Falha na integração — Rastreamento GPS', tempo: 'há 1h', cor: 'text-danger-500' },
    { id: 4, tipo: 'associado.cancelado', descricao: 'Cancelamento — Pedro Oliveira', tempo: 'há 2h', cor: 'text-warning-500' },
    { id: 5, tipo: 'pagamento.confirmado', descricao: 'Pagamento recebido — Ana Costa', tempo: 'há 3h', cor: 'text-primary-500' },
]

export function DashboardPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const tenant = useAuthStore((s) => s.tenant)

    useEffect(() => {
        setPageTitle('Dashboard')
    }, [setPageTitle])

    return (
        <div className="space-y-6">
            {/* Welcome */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-surface-900">
                        Bem-vindo, {tenant?.nome || 'Associação'}
                    </h2>
                    <p className="text-surface-700 text-sm mt-1">
                        Veja as principais métricas da sua associação
                    </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-surface-700">
                    <Activity size={16} className="text-success-500" />
                    <span>Sistema operacional</span>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card stat-card">
                    <div className="flex items-center justify-between">
                        <span className="stat-label">Associados Ativos</span>
                        <Users size={20} className="text-primary-500" />
                    </div>
                    <p className="stat-value">{mockStats.associadosAtivos.toLocaleString('pt-BR')}</p>
                    <p className="stat-change text-success-500 flex items-center gap-1">
                        <TrendingUp size={14} />
                        +23 este mês
                    </p>
                </div>

                <div className="card stat-card">
                    <div className="flex items-center justify-between">
                        <span className="stat-label">Veículos</span>
                        <Car size={20} className="text-accent-500" />
                    </div>
                    <p className="stat-value">{mockStats.totalVeiculos.toLocaleString('pt-BR')}</p>
                    <p className="stat-change text-success-500 flex items-center gap-1">
                        <TrendingUp size={14} />
                        +31 este mês
                    </p>
                </div>

                <div className="card stat-card">
                    <div className="flex items-center justify-between">
                        <span className="stat-label">Receita do Mês</span>
                        <Receipt size={20} className="text-success-500" />
                    </div>
                    <p className="stat-value text-2xl">{formatCurrency(mockStats.receitaMes)}</p>
                    <p className="stat-change text-success-500 flex items-center gap-1">
                        <TrendingUp size={14} />
                        +5.2% vs mês anterior
                    </p>
                </div>

                <div className="card stat-card">
                    <div className="flex items-center justify-between">
                        <span className="stat-label">Inadimplência</span>
                        <AlertTriangle size={20} className="text-warning-500" />
                    </div>
                    <p className="stat-value">{mockStats.inadimplencia}%</p>
                    <p className="stat-change text-danger-500 flex items-center gap-1">
                        <TrendingDown size={14} />
                        +1.2% vs mês anterior
                    </p>
                </div>
            </div>

            {/* Second row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Quick stats */}
                <div className="card p-6">
                    <h3 className="text-sm font-semibold text-surface-900 mb-4 flex items-center gap-2">
                        <FileText size={18} className="text-primary-500" />
                        Resumo de Contratos
                    </h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-success-500" />
                                <span className="text-sm text-surface-700">Ativos</span>
                            </div>
                            <span className="text-sm font-semibold">{mockStats.contratosAtivos}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Clock size={16} className="text-warning-500" />
                                <span className="text-sm text-surface-700">Inadimplentes</span>
                            </div>
                            <span className="text-sm font-semibold">{mockStats.associadosInadimplentes}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Receipt size={16} className="text-primary-500" />
                                <span className="text-sm text-surface-700">Faturas em Aberto</span>
                            </div>
                            <span className="text-sm font-semibold">{mockStats.faturasEmAberto}</span>
                        </div>
                    </div>
                </div>

                {/* Recent activity */}
                <div className="card p-6 lg:col-span-2">
                    <h3 className="text-sm font-semibold text-surface-900 mb-4 flex items-center gap-2">
                        <Activity size={18} className="text-primary-500" />
                        Atividade Recente
                    </h3>
                    <div className="space-y-3">
                        {recentActivities.map((activity) => (
                            <div
                                key={activity.id}
                                className="flex items-start gap-3 p-3 rounded-xl hover:bg-surface-50 transition-colors"
                            >
                                <div className={`mt-0.5 ${activity.cor}`}>
                                    {activity.tipo.includes('contrato') && <FileText size={16} />}
                                    {activity.tipo.includes('pagamento') && <Receipt size={16} />}
                                    {activity.tipo.includes('integracao') && <AlertTriangle size={16} />}
                                    {activity.tipo.includes('associado') && <Users size={16} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-surface-900">{activity.descricao}</p>
                                    <p className="text-xs text-surface-700/60 mt-0.5">{activity.tempo}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
